import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DEPTH,
    ORDER_STATUS_LANG_MAP,
    PRICE_TYPE,
    TRADE_TYPE,
    TRADE_TYPE_LANG_MAP,
} from 'src/common/constants';
import { currencyFormatter, DateTimeFormatter, contractNameFormatter } from 'src/common/helpers';
import { Button } from 'src/components/core/button';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { cancelActiveOrder } from 'src/setup/redux/slices/derivativesSlice';
import { ActiveOrder, FutureSymbol, OrderTableContentProps } from 'src/types';

const ActiveOrdersTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterActiveOrders, setFilterActiveOrders] = useState([] as Array<ActiveOrder>);
    const currentSymbol: FutureSymbol = useAppSelector((state) => state.derivatives.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const activeOrders: Array<ActiveOrder> = useAppSelector(
        (state) => state.derivatives.activeOrders,
    );
    const futureSymbols = useAppSelector((state) => state.global.configV2.futuresSymbol);
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? activeOrders
            : activeOrders.filter((activeOrder) => activeOrder.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterActiveOrders(filtered);
    }, [activeOrders, symbolId, isShowAll]);

    const cancelOrder = (orderId: string, type: string) => {
        dispatch(
            cancelActiveOrder({
                order_id: orderId,
                client_order_id: new Date().getTime(),
                type: type,
                account_index: userCurrentFutureTradeAccountIndex,
            }),
        );
    };

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return (
                    <div
                        className={classNames(
                            'contract',
                            {
                                borderGreen:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                borderRed:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {contractNameFormatter(row['symbolName'])}
                    </div>
                );
            },
        },
        {
            title: t('qty'),
            key: 'qty',
            width: '6%',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {currencyFormatter(
                            row['origQty'],
                            DEPTH[futureSymbols[row.symbolId]?.basePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            className: 'centerText',
            width: '9%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['price'],
                            DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('filled_total'),
            key: 'filledTotal',
            width: '9%',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {row['executedQty']}/{row['origQty']}
                    </span>
                );
            },
        },
        {
            title: t('trade_type'),
            key: 'tradeType',
            className: 'centerText',
            width: '9%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {t(TRADE_TYPE_LANG_MAP[row['side']])}
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            className: 'centerText',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return <span>{t(row['priceType'] === PRICE_TYPE.LIMIT ? 'limit' : 'market')}</span>;
            },
        },
        {
            title: t('status'),
            key: 'status',
            className: 'centerText',
            width: '9%',
            render: (row: { [x: string]: any }) => {
                return <span>{t(ORDER_STATUS_LANG_MAP[row['status']])}</span>;
            },
        },
        {
            title: t('order_no'),
            key: 'orderNumber',
            className: 'centerText',
            width: '12%',
            render: (row: { [x: string]: any }) => {
                return <span>{row['orderId']}</span>;
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            className: 'centerText',
            width: '12%',
            render: (row: { [x: string]: any }) => {
                return <span>{DateTimeFormatter(Number(row['time']))}</span>;
            },
        },
        {
            title: t('action'),
            key: 'action',
            className: 'centerText',
            width: '12%',
            render: (row: { [x: string]: any }) => {
                return (
                    <FlexContainer styles={{ justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => cancelOrder(row['orderId'], row['type'])}
                        >
                            {t('cancel')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];

    return <StyledTable columns={columns} data={filterActiveOrders} rowKey="orderId" />;
};

export { ActiveOrdersTable };
